import { useState, useEffect, useCallback } from 'react';
import { classService } from '../services/classService';
import { studentService } from '../services/studentService';
import { pushService } from '../services/pushService';
import { schoolService } from '../services/schoolService';
import { Questionnaire } from '../types';

const STORAGE_KEY = 'admin_onboarding_guide';

interface OnboardingGuideState {
  status: 'active' | 'skipped' | 'dismissed';
  completedSteps: string[];
}

export interface OnboardingStepDef {
  id: string;
  phase: number;
  title: string;
  description: string;
  route: string;
}

export const ONBOARDING_STEPS: OnboardingStepDef[] = [
  { id: 'create_school', phase: 1, title: '创建学校', description: '添加您管理的第一所学校', route: '/schools' },
  { id: 'create_class', phase: 1, title: '创建班级', description: '在学校中添加班级', route: '/schools' },
  { id: 'create_students', phase: 1, title: '添加学生', description: '导入或手动添加学生', route: '/schools' },
  { id: 'create_questionnaire', phase: 2, title: '创建问卷', description: '创建您的第一份问卷', route: '/questionnaires' },
  { id: 'configure_questions', phase: 2, title: '配置题目', description: '为问卷添加题目', route: '/questionnaires' },
  { id: 'publish_questionnaire', phase: 2, title: '发布问卷', description: '发布问卷以供填写', route: '/questionnaires' },
  { id: 'push_questionnaire', phase: 2, title: '推送问卷', description: '将问卷推送给学生', route: '/questionnaires' },
];

function loadState(): OnboardingGuideState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveState(state: OnboardingGuideState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function useOnboardingGuide(stats: {
  schoolCount: number;
  questionnaireCount: number;
  publishedCount: number;
  questionnaires: Questionnaire[];
}) {
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [guideStatus, setGuideStatus] = useState<'active' | 'skipped' | 'dismissed'>('active');

  // Initialize state from localStorage
  useEffect(() => {
    const saved = loadState();
    if (!saved) {
      saveState({ status: 'active', completedSteps: [] });
      setGuideStatus('active');
    } else if (saved.status === 'dismissed') {
      // Dismissed permanently - do NOT reset
      setGuideStatus('dismissed');
      setCompletedSteps(saved.completedSteps);
      setLoading(false);
      return;
    } else if (saved.status === 'skipped') {
      // Skipped last time: reset to active
      saveState({ status: 'active', completedSteps: saved.completedSteps });
      setGuideStatus('active');
      setCompletedSteps(saved.completedSteps);
    } else {
      setGuideStatus('active');
      setCompletedSteps(saved.completedSteps);
    }
  }, []);

  const refreshSteps = useCallback(async () => {
    // Check localStorage directly to avoid stale closure
    const saved = loadState();
    if (guideStatus === 'dismissed' || saved?.status === 'dismissed') {
      setLoading(false);
      return;
    }

    setLoading(true);
    const completed: string[] = [];

    try {
      // Phase 1 checks using stats + extra API calls
      if (stats.schoolCount > 0) {
        completed.push('create_school');
      }

      // Fetch extra data for class, student, and push checks
      const [academicYears, schools, pushes] = await Promise.all([
        classService.getAllAcademicYears().catch(() => []),
        stats.schoolCount > 0
          ? schoolService.getAllSchoolsSimple().catch(() => [])
          : Promise.resolve([]),
        pushService.getActivePushes(0, 1).catch(() => ({ totalElements: 0 })),
      ]);

      if (academicYears.length > 0) {
        completed.push('create_class');
      }

      // Check students: try first school
      if (schools.length > 0) {
        try {
          const studentData = await studentService.getStudents({
            schoolId: schools[0].id,
            page: 0,
            size: 1,
          });
          if (studentData.totalElements > 0) {
            completed.push('create_students');
          }
        } catch {
          // ignore
        }
      }

      // Phase 2 checks using stats
      if (stats.questionnaireCount > 0) {
        completed.push('create_questionnaire');
      }

      if (stats.questionnaires.some(q => q.questionCount > 0)) {
        completed.push('configure_questions');
      }

      if (stats.publishedCount > 0) {
        completed.push('publish_questionnaire');
      }

      if (pushes && (pushes as any).totalElements > 0) {
        completed.push('push_questionnaire');
      }

      setCompletedSteps(completed);
      saveState({ status: 'active', completedSteps: completed });
    } catch (error) {
      console.error('Failed to refresh onboarding steps:', error);
    } finally {
      setLoading(false);
    }
  }, [stats.schoolCount, stats.questionnaireCount, stats.publishedCount, stats.questionnaires, guideStatus]);

  // Refresh when stats change
  useEffect(() => {
    refreshSteps();
  }, [refreshSteps]);

  const skip = useCallback(() => {
    setGuideStatus('skipped');
    saveState({ status: 'skipped', completedSteps });
  }, [completedSteps]);

  const dismiss = useCallback(() => {
    setGuideStatus('dismissed');
    saveState({ status: 'dismissed', completedSteps });
  }, [completedSteps]);

  const isVisible = guideStatus === 'active' && !loading;

  return {
    isVisible,
    completedSteps,
    loading,
    skip,
    dismiss,
    refreshSteps,
  };
}
